import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useResponsive } from '../hooks/useMediaQuery';
import { getTheme } from '../theme';

/**
 * GameReactions – subtle, animated reaction bubbles on the game screen.
 *
 * Reactions are communicated via the room message channel using a special
 * `[REACTION]` prefix (e.g.  "[REACTION]🔥"). This avoids needing a new
 * database table or subscription – we piggyback on the existing messages infra.
 *
 * Props:
 *   roomMessages – current room messages array (from App state)
 *   currentUser  – logged-in username
 *   onSendReaction – function(reactionText) to send a reaction as a room message
 *   showReactions  – boolean toggle (from user settings)
 */

const REACTION_PREFIX = '[REACTION]';

const REACTIONS = [
  { emoji: '👍', labelKey: 'reactionNice' },
  { emoji: '🔥', labelKey: 'reactionHot' },
  { emoji: '🤯', labelKey: 'reactionWow' },
  { emoji: '🛡️', labelKey: 'reactionBlock' },
  { emoji: '😂', labelKey: 'reactionHaha' },
  { emoji: '😱', labelKey: 'reactionOhNo' },
];

const REACTION_COOLDOWN_MS = 3000; // 3 s between reactions

const GameReactions = ({ roomMessages, currentUser: _currentUser, onSendReaction, showReactions, t, compact = false, themeKey }) => {
  const { isMobile } = useResponsive();
  const theme = getTheme(themeKey);
  const [floatingReactions, setFloatingReactions] = useState([]);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const lastSendRef = useRef(0);
  const processedIdsRef = useRef(new Set());
  const idCounter = useRef(0);

  // Watch room messages for incoming reactions — spawn floating bubbles
  useEffect(() => {
    if (!showReactions || !roomMessages || roomMessages.length === 0) return;

    // Process only the last 5 messages to avoid replaying old reactions on mount
    const recent = roomMessages.slice(-5);
    recent.forEach(msg => {
      const msgId = msg.id || msg.created_at;
      if (processedIdsRef.current.has(msgId)) return;
      if (!msg.message?.startsWith(REACTION_PREFIX)) return;

      processedIdsRef.current.add(msgId);
      const emoji = msg.message.slice(REACTION_PREFIX.length);
      const uniqueId = ++idCounter.current;

      setFloatingReactions(prev => [
        ...prev,
        {
          id: uniqueId,
          emoji,
          username: msg.username,
          x: 20 + Math.random() * 60, // random horizontal position (%)
          createdAt: Date.now(),
        },
      ]);

      // Auto-remove after animation duration (2.5 s)
      setTimeout(() => {
        setFloatingReactions(prev => prev.filter(r => r.id !== uniqueId));
      }, 2500);
    });
    // Limit processed set size to prevent memory leak
    if (processedIdsRef.current.size > 200) {
      const arr = Array.from(processedIdsRef.current);
      processedIdsRef.current = new Set(arr.slice(-100));
    }
  }, [roomMessages, showReactions]);

  const handleReaction = useCallback((emoji) => {
    const now = Date.now();
    const elapsed = now - lastSendRef.current;
    if (elapsed < REACTION_COOLDOWN_MS) {
      const remaining = Math.ceil((REACTION_COOLDOWN_MS - elapsed) / 1000);
      setCooldownLeft(remaining);
      setTimeout(() => setCooldownLeft(0), remaining * 1000);
      return;
    }
    lastSendRef.current = now;
    setCooldownLeft(0);
    onSendReaction(`${REACTION_PREFIX}${emoji}`);
  }, [onSendReaction]);

  useEffect(() => {
    if (cooldownLeft > 0) setIsOpen(false);
  }, [cooldownLeft]);

  if (!showReactions) return null;

  return (
    <>
      {/* Floating reaction bubbles (positioned absolute over game area) */}
      {floatingReactions.map(r => (
        <div
          key={r.id}
          style={{
            position: 'fixed',
            left: `${r.x}%`,
            bottom: isMobile ? '80px' : '100px',
            zIndex: 850,
            pointerEvents: 'none',
            animation: 'reactionFloat 2.5s ease-out forwards',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          <span style={{ fontSize: isMobile ? '28px' : '34px', filter: `drop-shadow(0 2px 4px ${theme.surfaces.overlay})` }}>
            {r.emoji}
          </span>
          <span style={{
            fontSize: '10px',
            color: theme.text.muted,
            background: theme.surfaces.surface1,
            border: `1px solid ${theme.borders.default}`,
            borderRadius: '4px',
            padding: '1px 5px',
            whiteSpace: 'nowrap',
          }}>
            {r.username}
          </span>
        </div>
      ))}

      {/* Reaction controls — compact toggle (chat-like) or inline fallback */}
      {compact ? (
        <div style={{
          position: 'relative',
          opacity: cooldownLeft > 0 ? 0.6 : 1,
          transition: 'opacity 0.2s',
        }}>
          <button
            onClick={() => setIsOpen(v => !v)}
            title={t('gameReactionsLabel')}
            disabled={cooldownLeft > 0}
            style={{
              background: isOpen ? theme.accents.soft : theme.surfaces.surface1,
              border: `1px solid ${isOpen ? theme.accents.softBorder : theme.borders.default}`,
              color: theme.text.body,
              borderRadius: '6px',
              padding: isMobile ? '4px 8px' : '5px 10px',
              cursor: cooldownLeft > 0 ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              lineHeight: 1,
              minHeight: isMobile ? '28px' : '30px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
              whiteSpace: 'nowrap',
            }}
          >
            <span>😀</span>
            <span style={{ fontSize: isMobile ? '10px' : '11px', opacity: 0.85, color: theme.text.muted }}>{isOpen ? '▲' : '▼'}</span>
          </button>

          {isOpen && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              zIndex: 20,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '4px',
              background: theme.gradients.modal,
              border: `1px solid ${theme.borders.default}`,
              borderRadius: '8px',
              padding: '6px',
              boxShadow: theme.shadows.card,
              minWidth: isMobile ? '112px' : '126px',
              backdropFilter: 'blur(6px)',
            }}>
              {REACTIONS.map(r => (
                <button
                  key={r.emoji}
                  onClick={() => {
                    handleReaction(r.emoji);
                    setIsOpen(false);
                  }}
                  title={t(r.labelKey)}
                  style={{
                    background: theme.surfaces.surface0,
                    border: `1px solid ${theme.borders.default}`,
                    borderRadius: '6px',
                    padding: isMobile ? '5px 6px' : '6px 8px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '15px' : '16px',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: isMobile ? '30px' : '32px',
                  }}
                >
                  {r.emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{
          display: 'flex',
          gap: isMobile ? '3px' : '4px',
          alignItems: 'center',
          flexWrap: 'wrap',
          opacity: cooldownLeft > 0 ? 0.5 : 1,
          transition: 'opacity 0.2s',
        }}>
          {REACTIONS.map(r => (
            <button
              key={r.emoji}
              onClick={() => handleReaction(r.emoji)}
              disabled={cooldownLeft > 0}
              title={t(r.labelKey)}
              style={{
                background: theme.surfaces.surface0,
                border: `1px solid ${theme.borders.default}`,
                borderRadius: '6px',
                padding: isMobile ? '3px 6px' : '4px 8px',
                cursor: cooldownLeft > 0 ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                lineHeight: 1,
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                if (cooldownLeft > 0) return;
                e.currentTarget.style.background = theme.accents.soft;
                e.currentTarget.style.borderColor = theme.accents.softBorder;
                e.currentTarget.style.transform = 'scale(1.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = theme.surfaces.surface0;
                e.currentTarget.style.borderColor = theme.borders.default;
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {r.emoji}
            </button>
          ))}
        </div>
      )}

      {/* CSS keyframes for float animation injected once */}
      <style>{`
        @keyframes reactionFloat {
          0%   { opacity: 1; transform: translateY(0) scale(1); }
          60%  { opacity: 1; }
          100% { opacity: 0; transform: translateY(-120px) scale(1.3); }
        }
      `}</style>
    </>
  );
};

export { REACTION_PREFIX };
export default GameReactions;
